import CarouselNavButtons from '../CarouselNavButtons/CarouselNavButtons';
import CarouselSideButtons from '../CarouselNavButtons/CarouselSideButtons';
import HomeHorizontalScroll from '../HomeHorizontalScroll/HomeHorizontalScroll';
import { useHomeHorizontalScrollNav } from '../HomeHorizontalScroll/useHomeHorizontalScrollNav';
import { Children, isValidElement, type FC, type ReactNode } from 'react';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

interface SurveysCarouselProps {
  children: ReactNode;
  contentKey?: string | number;
  sectionTitle?: ReactNode;
  stacked?: boolean;
}

const SurveysCarousel: FC<SurveysCarouselProps> = ({ children, contentKey, sectionTitle, stacked = false }) => {
  const t = useTranslationByKey('PAGES.HOME');
  const { scrollRef, onPrev, onNext, isPrevDisabled, isNextDisabled } = useHomeHorizontalScrollNav(contentKey);
  const items = Children.toArray(children).filter(isValidElement);
  const itemCount = items.length;
  const hasItems = itemCount > 0;
  const layout = itemCount <= 2 ? 'single-row' : 'two-row-grid';
  const showMobileCarouselNav = hasItems && !stacked;

  return (
    <>
      {sectionTitle && (
        <div className="mb-2 flex w-full items-center justify-between gap-2 md:hidden">
          {sectionTitle}
          {showMobileCarouselNav && (
            <CarouselNavButtons
              onPrev={onPrev}
              onNext={onNext}
              isPrevDisabled={isPrevDisabled}
              isNextDisabled={isNextDisabled}
              prevAriaLabel={t('CAROUSEL_PREVIOUS')}
              nextAriaLabel={t('CAROUSEL_NEXT')}
              variant="floating"
            />
          )}
        </div>
      )}
      {stacked ? (
        <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-[repeat(auto-fit,minmax(280px,320px))] md:gap-5">
          {items.map((child, index) => (
            <div key={child.key ?? index} className="w-full">
              {child}
            </div>
          ))}
        </div>
      ) : (
        <div className="relative w-full max-w-full min-w-0">
          <HomeHorizontalScroll
            layout={layout}
            itemClassName="w-[280px] shrink-0 md:w-[320px]"
            showEdgePadding
            scrollRef={scrollRef}
            mobileScrollMode="buttons"
          >
            {children}
          </HomeHorizontalScroll>
          {hasItems && (
            <CarouselSideButtons
              onPrev={onPrev}
              onNext={onNext}
              isPrevDisabled={isPrevDisabled}
              isNextDisabled={isNextDisabled}
              prevAriaLabel={t('CAROUSEL_PREVIOUS')}
              nextAriaLabel={t('CAROUSEL_NEXT')}
              className="-left-4 -right-4"
            />
          )}
        </div>
      )}
    </>
  );
};

export default SurveysCarousel;
